import { useCallback, useEffect, useRef, useState } from 'react';
import { AppState } from 'react-native';
import NetInfo from 'app/services/IsolatedNetInfo';
import { DEVICE_ISSUE_TYPE, TDeviceIssue } from 'app/types/deviceIssue';
import { useDataFreshness, usePathwayId } from 'app/_modules/pathways';
import {
  BATTERY_LOW_THRESHOLD,
  BATTERY_POLL_INTERVAL_MS,
  GATEWAY_POLL_INTERVAL_MS,
  WIFI_POLL_INTERVAL_MS,
  WIFI_WEAK_CONFIRM_MS,
  WIFI_WEAK_STRENGTH_THRESHOLD,
  extractWifiStrength,
  readBatteryLevel,
  readWifiStrength,
} from '../utils/deviceSignals';

export const useDeviceIssues = (): TDeviceIssue[] => {
  const [tabletBatteryLow, setTabletBatteryLow] = useState(false);
  const [routerWeakSignal, setRouterWeakSignal] = useState(false);
  const weakSince = useRef<number | null>(null);
  const pathwayId = usePathwayId();
  const {
    data: dataFreshness,
    isError: isDataFreshnessError,
    refetch: refetchDataFreshness,
  } = useDataFreshness(pathwayId, { refetchInterval: GATEWAY_POLL_INTERVAL_MS });
  const gatewayDisconnected = !isDataFreshnessError && dataFreshness?.isGatewayConnected === false;

  const checkBatteryLevel = useCallback(async () => {
    const level = await readBatteryLevel();
    setTabletBatteryLow(level >= 0 && level < BATTERY_LOW_THRESHOLD);
  }, []);

  const applyWifiStrength = useCallback((strength: number | null) => {
    if (strength === null || strength >= WIFI_WEAK_STRENGTH_THRESHOLD) {
      weakSince.current = null;
      setRouterWeakSignal(false);
      return;
    }

    if (weakSince.current === null) weakSince.current = Date.now();
    setRouterWeakSignal(Date.now() - weakSince.current >= WIFI_WEAK_CONFIRM_MS);
  }, []);

  const checkWifiStrength = useCallback(async () => {
    applyWifiStrength(await readWifiStrength());
  }, [applyWifiStrength]);

  useEffect(() => {
    checkBatteryLevel();
    const interval = setInterval(checkBatteryLevel, BATTERY_POLL_INTERVAL_MS);
    const subscription = AppState.addEventListener('change', (state) => {
      if (state === 'active') checkBatteryLevel();
    });

    return () => {
      clearInterval(interval);
      subscription.remove();
    };
  }, [checkBatteryLevel]);

  useEffect(() => {
    const subscription = AppState.addEventListener('change', (state) => {
      if (state === 'active') refetchDataFreshness();
    });

    return () => subscription.remove();
  }, [refetchDataFreshness]);

  useEffect(() => {
    checkWifiStrength();
    const interval = setInterval(checkWifiStrength, WIFI_POLL_INTERVAL_MS);
    const unsubscribeNetInfo = NetInfo.addEventListener((state) => applyWifiStrength(extractWifiStrength(state)));
    const appStateSubscription = AppState.addEventListener('change', (state) => {
      if (state === 'active') checkWifiStrength();
    });

    return () => {
      clearInterval(interval);
      unsubscribeNetInfo();
      appStateSubscription.remove();
    };
  }, [checkWifiStrength, applyWifiStrength]);

  return [
    { issueType: DEVICE_ISSUE_TYPE.gatewayDisconnected, active: gatewayDisconnected },
    { issueType: DEVICE_ISSUE_TYPE.tabletBatteryLow, active: tabletBatteryLow },
    { issueType: DEVICE_ISSUE_TYPE.routerWeakSignal, active: routerWeakSignal },
  ];
};
